import {
  Badge,
  Box,
  HStack,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { RiChat2Line } from "react-icons/ri";
import type { Point } from "src/components/DocumentViewer/V2/types";
import { PersonProfileLink } from "src/components/Links/PersonProfileLink";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { AIValidationResult } from "src/hooks/useDocumentAI";
import { useSendMessage } from "src/hooks/useSendMessage";
import { isLinkedLogin } from "src/services/person";
import * as GQL from "src/types/graphql";
import { IdentityDocumentMatchHint } from "../IdentityDocumentMatchHint";
import {
  IdentityAnalyzeIdMatch,
  Profile,
  ProfileFieldType,
} from "../utils/analyzeIdFormMatchUtils";
import { AddressWithAISuggestion } from "./AddressWithAISuggestion";
import { useAddressAI } from "./useAddressAI";

type ParentPanelProps = {
  parents: Profile[];
  formId: string;
  aiResultsMap?: Record<string, AIValidationResult> | null;
  identityAiProfileMatches?: IdentityAnalyzeIdMatch[];
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

type GuardianInfoProps = {
  guardian: Profile;
  formId: string;
  aiResultsMap?: Record<string, AIValidationResult> | null;
  identityAiProfileMatches?: IdentityAnalyzeIdMatch[];
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

// Guardian info component that can use hooks
const GuardianInfo: React.FC<GuardianInfoProps> = ({
  guardian,
  formId,
  aiResultsMap,
  identityAiProfileMatches,
  onFindInDocument,
}) => {
  const { handleSendMessage } = useSendMessage();

  // Function to handle send message click
  const onSendMessageClick = () => {
    handleSendMessage([formId]);
  };

  // Use hook for address AI processing
  const { bestAddressAI, suggestedAddress } = useAddressAI(
    guardian?.id,
    aiResultsMap
  );

  const nameMatch = identityAiProfileMatches?.find(
    (match) => match.profileFieldType === ProfileFieldType.Name
  );
  const addressMatch = identityAiProfileMatches?.find(
    (match) => match.profileFieldType === ProfileFieldType.Address
  );

  return (
    <Box p={4}>
      <HStack spacing={2} mb={2}>
        <Text fontSize="2xl" fontWeight="bold" color="primary.700">
          {guardian.first_name || ""} {guardian.last_name || ""}
        </Text>
        <PersonProfileLink
          personId={guardian?.id}
          personType={GQL.person_type_enum.guardian}
          buttonSize="lg"
        />
      </HStack>
      {nameMatch ? (
        <IdentityDocumentMatchHint
          documentMatchText={nameMatch.documentMatchText}
          matchHighlights={nameMatch.matchHighlights}
          onFindInDocument={onFindInDocument}
        />
      ) : null}

      <Box my={3}>
        <HStack spacing={1} mb={1}>
          <Text fontSize="sm" color="gray.600">
            Email
          </Text>
          {isLinkedLogin(guardian.email_address, guardian.user?.name) && (
            <Badge colorScheme="gray" variant="solid" ml="1rem">
              LINKED
            </Badge>
          )}
        </HStack>
        <HStack justifyContent="space-between">
          <Text fontSize="md">{guardian.email_address || "N/A"}</Text>
          <WithUserPermissions permissions={["message:create"]}>
            {guardian.email_okay && (
              <Tooltip label="Send Message">
                <IconButton
                  aria-label="send-message"
                  icon={<RiChat2Line />}
                  size="sm"
                  variant="ghost"
                  onClick={onSendMessageClick}
                />
              </Tooltip>
            )}
          </WithUserPermissions>
        </HStack>
      </Box>

      {guardian.phone_number && (
        <Box my={3}>
          <HStack spacing={1} mb={1}>
            <Text fontSize="sm" color="gray.600">
              Phone
            </Text>
            {isLinkedLogin(guardian.phone_number, guardian.user?.name) && (
              <Badge colorScheme="gray" variant="solid" ml="1rem">
                LINKED
              </Badge>
            )}
          </HStack>
          <HStack justifyContent="space-between">
            <Text fontSize="md">{guardian.phone_number}</Text>
            <WithUserPermissions permissions={["message:create"]}>
              {guardian.sms_okay && (
                <Tooltip label="Send Message">
                  <IconButton
                    aria-label="send-message"
                    icon={<RiChat2Line />}
                    size="sm"
                    variant="ghost"
                    onClick={onSendMessageClick}
                  />
                </Tooltip>
              )}
            </WithUserPermissions>
          </HStack>
        </Box>
      )}

      <AddressWithAISuggestion
        person={guardian}
        bestAddressAI={bestAddressAI ?? null}
        suggestedAddress={suggestedAddress}
      />
      {addressMatch ? (
        <IdentityDocumentMatchHint
          documentMatchText={addressMatch.documentMatchText}
          matchHighlights={addressMatch.matchHighlights}
          onFindInDocument={onFindInDocument}
        />
      ) : null}
    </Box>
  );
};

export const ParentPanel: React.FC<ParentPanelProps> = ({
  parents,
  formId,
  aiResultsMap,
  identityAiProfileMatches,
  onFindInDocument,
}) => {
  return parents.length > 0 ? (
    <VStack spacing={6} align="stretch">
      {parents.map((parent) => (
        <GuardianInfo
          key={parent.id}
          guardian={parent}
          formId={formId}
          aiResultsMap={aiResultsMap}
          identityAiProfileMatches={identityAiProfileMatches?.filter(
            (match) => match.id === parent.id
          )}
          onFindInDocument={onFindInDocument}
        />
      ))}
    </VStack>
  ) : (
    <Box p={4} textAlign="center">
      <Text color="gray.600">Parent information not available</Text>
    </Box>
  );
};
